/**
 * AbilityLoader — the one place an ability id becomes callable on the client
 * (action-bar plan phase 1, docs/plans/2026-10-23-action-bar-and-ability-loading).
 *
 * `ensure(ids)` always runs the same order: behaviors once, then the
 * registration file that owns each authored id (from the generated
 * `authoredAbilityIndex.gen.ts`), then a TOML generic for every id with no
 * authored module. Icons resolve through the registry's icon resolver.
 *
 * Precedence, written once: an authored module beats a TOML generic, for every
 * class that has the id (D3). When an authored load replaces a generic that a
 * lookup had already built, it says so in the log.
 *
 * Every id it was asked about has a status (`authored`, `toml` or `failed`),
 * published on `window.__GAME_BOOT__.abilityStatus` for the debug panel.
 */
import { ABILITY_DATA, getAbilitiesForClass } from '../../../../../shared/content/generated/abilities.gen';
import { ABILITY_UNLOCK_LEVELS, DEFAULT_HOTBAR_SLOTS } from '../../../../../shared/content/generated/hotbars.gen';
import { PEACEFUL_HOTBAR_ABILITY_IDS } from '../../../../../shared/logic/peacefulHotbar';
import { gameLogger } from '../../../../../utils/GameLogger';
import { abilityRegistry } from '../AbilityRegistry';
import { loadAllBehaviors } from '../BehaviorRegistry';
import { ensureTomlAbility, registerTomlAbilities } from '../TomlAbilityFactory';
import { applyStarterIcons } from './applyStarterIcons';
import { AUTHORED_ABILITY_REGISTRAR, AUTHORED_ABILITY_REGISTRARS } from './authoredAbilityIndex.gen';

export type AbilityLoadStatus =
  | { source: 'authored'; registrar: string }
  | { source: 'toml' }
  | { source: 'failed'; reason: string };

export interface Registrar {
  exportName: string;
  load: () => Promise<Record<string, unknown>>;
}

export interface AbilityBootOptions {
  playerClass: string;
  /** Peaceful, biome-tour and race sessions show the shared movement bar. */
  sessionMovementBar: boolean;
}

export interface AbilityBootResult {
  className: string;
  /** Export names of the registration files the boot loaded. */
  registrars: string[];
}

const TOML_TAG = 'toml-factory';

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** Every id a fresh session of this class can show before the server's learned list arrives. */
export function bootAbilityIds(options: AbilityBootOptions): string[] {
  const className = options.playerClass.trim().toLowerCase();
  return [...new Set([
    ...Object.keys(getAbilitiesForClass(className)),
    ...(ABILITY_UNLOCK_LEVELS[className] ?? []).map(unlock => unlock.abilityId),
    ...(DEFAULT_HOTBAR_SLOTS[className] ?? []).flatMap(slot => (slot.abilityId ? [slot.abilityId] : [])),
    ...(options.sessionMovementBar ? PEACEFUL_HOTBAR_ABILITY_IDS : []),
  ])];
}

export class AbilityLoader {
  private readonly statuses = new Map<string, AbilityLoadStatus>();
  private readonly registrarLoads = new Map<string, Promise<void>>();
  private readonly registrarFailures = new Map<string, string>();

  constructor(
    private readonly registrars: Readonly<Record<string, Registrar>> = AUTHORED_ABILITY_REGISTRARS,
    private readonly owners: Readonly<Record<string, string>> = AUTHORED_ABILITY_REGISTRAR,
  ) {}

  /** Make every id callable, in the one fixed order. Never throws for a single bad id. */
  async ensure(ids: Iterable<string>): Promise<ReadonlyMap<string, AbilityLoadStatus>> {
    const wanted = [...new Set(ids)].filter(Boolean);
    applyStarterIcons();
    await loadAllBehaviors();
    const keys = new Set(wanted.flatMap(id => (this.owners[id] ? [this.owners[id]] : [])));
    await Promise.all([...keys].map(key => this.loadRegistrar(key)));
    for (const id of wanted) this.statuses.set(id, this.resolve(id));
    this.publish();
    return new Map(wanted.map(id => [id, this.statuses.get(id)!]));
  }

  /** Boot: the class's ids (and the session movement bar), then the auto-attack table. */
  async boot(options: AbilityBootOptions): Promise<AbilityBootResult> {
    const ids = bootAbilityIds(options);
    await this.ensure(ids);
    // Every class's auto-attack, as before the loader (resolved lazily for other classes).
    registerTomlAbilities({ onlyAutoAttacks: true });
    const keys = [...new Set(ids.flatMap(id => (this.owners[id] ? [this.owners[id]] : [])))].sort();
    return {
      className: options.playerClass.trim().toLowerCase(),
      registrars: keys.filter(key => !this.registrarFailures.has(key)).map(key => this.registrars[key].exportName),
    };
  }

  status(id: string): AbilityLoadStatus | undefined {
    return this.statuses.get(id);
  }

  allStatuses(): Readonly<Record<string, AbilityLoadStatus>> {
    return Object.fromEntries([...this.statuses].sort(([a], [b]) => a.localeCompare(b)));
  }

  private loadRegistrar(key: string): Promise<void> {
    const existing = this.registrarLoads.get(key);
    if (existing) return existing;
    const loading = this.importAndRegister(key).catch((error: unknown) => {
      // Not cached: a later ensure retries the import.
      this.registrarLoads.delete(key);
      const reason = `${this.registrars[key]?.exportName ?? key} failed to load: ${errorMessage(error)}`;
      this.registrarFailures.set(key, reason);
      gameLogger.warn('abilities', `AbilityLoader: ${reason}`);
    });
    this.registrarLoads.set(key, loading);
    return loading;
  }

  private async importAndRegister(key: string): Promise<void> {
    const registrar = this.registrars[key];
    if (!registrar) throw new Error(`no registration file "${key}" in the authored ability index`);
    const module = await registrar.load();
    const register = module[registrar.exportName];
    if (typeof register !== 'function') throw new Error(`does not export callable ${registrar.exportName}`);
    const owned = Object.keys(this.owners).filter(id => this.owners[id] === key);
    // isRegistered first: getMetadata on an unregistered id would build a generic.
    const genericsBefore = owned.filter(id =>
      abilityRegistry.isRegistered(id) && abilityRegistry.getMetadata(id)?.tags?.includes(TOML_TAG));
    register();
    this.registrarFailures.delete(key);
    if (genericsBefore.length > 0) {
      gameLogger.info('abilities', `AbilityLoader: ${registrar.exportName} replaced TOML generics: ${genericsBefore.join(', ')}`);
    }
  }

  private resolve(id: string): AbilityLoadStatus {
    const key = this.owners[id];
    const failure = key ? this.registrarFailures.get(key) : undefined;
    if (key && !failure && abilityRegistry.isRegistered(id)) return { source: 'authored', registrar: key };
    if (!ABILITY_DATA[id]) {
      return { source: 'failed', reason: failure ?? (key ? `${id} was not registered by ${key}` : 'no authored module or TOML row') };
    }
    const generic = ensureTomlAbility(id) !== undefined;
    if (failure) return { source: 'failed', reason: `${failure}${generic ? '; TOML generic in use' : ''}` };
    if (key) return { source: 'failed', reason: `${id} was not registered by ${key}` };
    return generic ? { source: 'toml' } : { source: 'failed', reason: 'TOML generic failed to build' };
  }

  private publish(): void {
    if (typeof window === 'undefined') return;
    const boot = (window as { __GAME_BOOT__?: { abilityStatus?: unknown } }).__GAME_BOOT__;
    if (boot) boot.abilityStatus = this.allStatuses();
  }
}

/** The session's loader. PreloadScene boots it; the learned list ensures through it. */
export const abilityLoader = new AbilityLoader();
